import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Card,
    Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button,
    Box, CircularProgress, Snackbar, Alert, Radio, RadioGroup, FormControlLabel,
    FormHelperText
} from '@mui/material';
import { CloudUpload, Delete, Add, Close, Warning } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { PUT, POST } from 'APIclient';
import properties from 'constants/ApplicationConstants';

const AdditionalItemsSection = ({
    items,
    handleAdditionalItemChange,
    handleRemoveAdditionalItem,
    handleAddAdditionalItem,
    validationErrors,
    disabled
}) => {
    return (
        <>
            {items.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mt: 0, alignItems: 'center', borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth variant="standard" placeholder="Enter particular"
                            value={item.particular || item.itemName || ''}
                            onChange={(e) => handleAdditionalItemChange(index, 'particular', e.target.value)}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth variant="standard" error={validationErrors[`additionalItem_${index}_status`]}>
                            <InputLabel>Select Status</InputLabel>
                            <Select
                                value={item.status || ''}
                                onChange={(e) => handleAdditionalItemChange(index, 'status', e.target.value)}
                                disabled={disabled}
                            >
                                {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                <MenuItem value="N/A">N/A</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                                <MenuItem value="Submitted">Submitted</MenuItem>
                            </Select>
                            {validationErrors[`additionalItem_${index}_status`] && <FormHelperText>Please select status</FormHelperText>}
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth variant="standard" placeholder="Employee comment"
                            value={item.employeeComment || ''} disabled
                            sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth variant="standard" placeholder="Enter department comment"
                            value={item.departmentComment || ''}
                            onChange={(e) => handleAdditionalItemChange(index, 'departmentComment', e.target.value)}
                            error={validationErrors[`additionalItem_${index}_departmentComment`]}
                            helperText={validationErrors[`additionalItem_${index}_departmentComment`] ? 'Required' : ''}
                            disabled={disabled}
                        />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <IconButton size="small" onClick={() => handleRemoveAdditionalItem(index)} color="error" disabled={disabled}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Grid>
                </Grid>
            ))}
            <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAddAdditionalItem}
                sx={{ mt: 1, color: 'rgba(158, 97, 97, 0.93)', borderColor: 'black', '&:hover': { borderColor: 'black', backgroundColor: 'rgba(0,0,0,0.04)' } }} disabled={disabled}>
                Add New Row
            </Button>
        </>
    );
};

const AdministrationClearanceUpdateForm = ({ open, onClose, clearanceData, onSuccess, readOnly = false }) => {
    const [loading, setLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackColor, setSnackColor] = useState('success');
    const [snackMessage, setSnackMessage] = useState('');
    const [isAlreadyApproved, setIsAlreadyApproved] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [openSignatureDialog, setOpenSignatureDialog] = useState(false);
    const [openSimpleSignatureDialog, setOpenSimpleSignatureDialog] = useState(false);
    const [existingSignature, setExistingSignature] = useState(null);
    const [signatureChoice, setSignatureChoice] = useState('existing');
    const [newSignature, setNewSignature] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const empId = localStorage.getItem("empId") || "";
    const existingEmpId = clearanceData?.empId;

    const [formData, setFormData] = useState({
        empId: empId,
        idCard: '',
        departmentIdCardComment: '',
        drawerKeys: '',
        departmentDrawerKeysComment: '',
        accessCardDeleted: '',
        departmentAccessCardComment: '',
        companyMobile: '',
        departmentCompanyMobileComment: '',
        stationery: '',
        departmentStationeryComment: '',
        additionalItems: [],
        clearanceDate: dayjs(),
    });

    // Map backend YES/NO/NA → frontend
    const mapBackendToFrontend = (status) => {
        switch (status?.toUpperCase()) {
            case 'YES': return 'Submitted';
            case 'NO': return 'Pending';
            case 'NA': return 'N/A';
            default: return '';
        }
    };

    useEffect(() => {
        if (clearanceData) {
            const wasApproved = ['approved', 'cleared'].includes(clearanceData.status?.toLowerCase());
            setIsAlreadyApproved(wasApproved);

            setFormData({
                empId,
                idCard: mapBackendToFrontend(clearanceData.idCardStatus),
                departmentIdCardComment: clearanceData.departmentIdCardComment || '',
                drawerKeys: mapBackendToFrontend(clearanceData.drawerKeysStatus),
                departmentDrawerKeysComment: clearanceData.departmentDrawerKeysComment || '',
                // accessCardDeleted: mapBackendToFrontend(clearanceData.accessCardStatus),
                // departmentAccessCardComment: clearanceData.departmentAccessCardComment || '',
                // companyMobile: mapBackendToFrontend(clearanceData.companyMobileStatus),
                // departmentCompanyMobileComment: clearanceData.departmentCompanyMobileComment || '',
                stationery: mapBackendToFrontend(clearanceData.stationeryStatus),
                departmentStationeryComment: clearanceData.departmentStationeryComment || '',
                additionalItems: (clearanceData.additionalItems || []).map(item => ({
                    ...item,
                    status: mapBackendToFrontend(item.status)
                })),
                clearanceDate: clearanceData.clearanceDate ? dayjs(clearanceData.clearanceDate) : dayjs(),
            });

            setExistingSignature(wasApproved ? clearanceData.managerSignature : null);
            setHasChanges(false);
            setSignatureChoice('existing');
            setNewSignature(null);
            setValidationErrors({});
        }
    }, [clearanceData, empId]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setHasChanges(true);
        if (validationErrors[field]) {
            setValidationErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
        }
    };

    const handleSignatureDialogCancel = () => {
        setOpenSignatureDialog(false);
        setSignatureChoice('existing');
        setNewSignature(null);
    };

    const handleSignatureDialogConfirm = async () => {
        if (signatureChoice === 'new' && !newSignature) {
            setSnackMessage('Please upload a new signature');
            setSnackColor('error');
            setSnackOpen(true);
            return;
        }

        setOpenSignatureDialog(false);

        const signatureToUse = signatureChoice === 'existing' ? existingSignature : newSignature;

        await performUpdate(signatureToUse, true);
    };

    const handleAdditionalChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            additionalItems: prev.additionalItems.map((it, i) => i === index ? { ...it, [field]: value } : it)
        }));
        setHasChanges(true);
        const key = `additionalItem_${index}_${field === 'status' ? 'status' : 'departmentComment'}`;
        if (validationErrors[key]) {
            setValidationErrors(prev => { const e = { ...prev }; delete e[key]; return e; });
        }
    };

    const handleAddAdditional = () => {
        setFormData(prev => ({
            ...prev,
            additionalItems: [...prev.additionalItems, { particular: '', status: '', employeeComment: '', departmentComment: '' }]
        }));
        setHasChanges(true);
    };

    const handleRemoveAdditional = (index) => {
        setFormData(prev => ({
            ...prev,
            additionalItems: prev.additionalItems.filter((_, i) => i !== index)
        }));
        setHasChanges(true);
        setValidationErrors(prev => {
            const e = { ...prev };
            delete e[`additionalItem_${index}_status`];
            delete e[`additionalItem_${index}_departmentComment`];
            return e;
        });
    };

    const mapFrontendToBackend = (status) => {
        switch (status) {
            case 'Submitted': return 'YES';
            case 'Pending': return 'NO';
            case 'N/A': return 'NA';
            default: return 'NA';
        }
    };

    const validateForm = () => {
        const errors = {};
        const fields = [
            { key: 'idCard', comment: 'departmentIdCardComment' },
            { key: 'drawerKeys', comment: 'departmentDrawerKeysComment' },
            // { key: 'accessCardDeleted', comment: 'departmentAccessCardComment' },
            // { key: 'companyMobile', comment: 'departmentCompanyMobileComment' },
            { key: 'stationery', comment: 'departmentStationeryComment' },
        ];

        fields.forEach(f => {
            if (!formData[f.key]) errors[f.key] = true;
            if (!formData[f.comment]?.trim()) errors[f.comment] = true;
        });

        formData.additionalItems.forEach((item, i) => {
            if (!item.status) errors[`additionalItem_${i}_status`] = true;
            if (!item.departmentComment?.trim()) errors[`additionalItem_${i}_departmentComment`] = true;
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };


    const checkForPendingItems = () => {
        const pendingItems = [];

        const mainFields = [
            { key: 'idCard', name: 'Id card' },
            { key: 'drawerKeys', name: 'Drawer keys' },
            // { key: 'accessCardDeleted', name: 'Access cards / Biometric' },
            // { key: 'companyMobile', name: 'Company mobile phone' },
            { key: 'stationery', name: 'Stationery' },
        ];

        mainFields.forEach(({ key, name }) => {
            if (formData[key] === 'Pending') {
                pendingItems.push(name);
            }
        });

        formData.additionalItems.forEach((item, index) => {
            if (item.status === 'Pending') {
                const itemName = item.particular || `Additional Item ${index + 1}`;
                pendingItems.push(itemName);
            }
        });

        return pendingItems;
    };

    const handleSubmit = async (e, isApprove = false) => {
        e.preventDefault();


        if (!isApprove) {
            //     if (!validateForm()) {
            //     setSnackMessage('Please fill all required fields');
            //     setSnackColor('error');
            //     setSnackOpen(true);
            //     return;
            // }

            await performUpdate(null, false);
            return;
        }

        const pendingItems = checkForPendingItems();
        if (pendingItems.length > 0) {
            const pendingList = pendingItems.map((item, idx) => `${idx + 1}. ${item}`).join('\n');
            setSnackMessage(
                `Cannot approve clearance. The following items are still pending:\n\n${pendingList}\n\nPlease update all pending items before approving.`
            );
            setSnackColor('error');
            setSnackOpen(true);
            return;
        }

        if (!validateForm()) {
            setSnackMessage('Please fill all required fields');
            setSnackColor('error');
            setSnackOpen(true);
            return;
        }

        if (isAlreadyApproved) {
            setOpenSignatureDialog(true);
        } else {
            setOpenSimpleSignatureDialog(true);
        }
    };

    const performUpdate = async (signature = null, shouldApprove = false) => {
        if (shouldApprove) setApproveLoading(true);
        else setUpdateLoading(true);

        try {
            const token = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');
            const payload = {
                empId,
                idCardStatus: mapFrontendToBackend(formData.idCard),
                empIdCardComment: clearanceData?.empIdCardComment,
                departmentIdCardComment: formData.departmentIdCardComment,
                drawerKeysStatus: mapFrontendToBackend(formData.drawerKeys),
                empDrawerKeysComment: clearanceData?.empDrawerKeysComment,
                departmentDrawerKeysComment: formData.departmentDrawerKeysComment,
                // accessCardStatus: mapFrontendToBackend(formData.accessCardDeleted),
                // empAccessCardComment: clearanceData?.empAccessCardComment,
                // departmentAccessCardComment: formData.departmentAccessCardComment,
                // companyMobileStatus: mapFrontendToBackend(formData.companyMobile),
                // empCompanyMobileComment: clearanceData?.empCompanyMobileComment,
                // departmentCompanyMobileComment: formData.departmentCompanyMobileComment,
                stationeryStatus: mapFrontendToBackend(formData.stationery),
                empStationeryComment: clearanceData?.empStationeryComment,
                departmentStationeryComment: formData.departmentStationeryComment,
                additionalItems: formData.additionalItems.map(item => ({
                    ...item,
                    status: mapFrontendToBackend(item.status)
                })),
                clearanceDate: formData.clearanceDate.format('YYYY-MM-DD'),
            };

            await PUT(`api/exit/clearance/administration/${clearanceData.exitClearanceId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setSnackMessage('Administration clearance updated successfully');
            setSnackColor('success');
            setSnackOpen(true);

            if (shouldApprove && signature) {
                await performApproval(signature);
            } else {
                setTimeout(() => { onSuccess(); onClose(); }, 1500);
            }
        } catch (err) {
            setSnackMessage(err.response?.data?.message || 'Update failed');
            setSnackColor('error');
            setSnackOpen(true);
        } finally {
            setUpdateLoading(false);
            setApproveLoading(false);
        }
    };

    const performApproval = async (signature) => {
        try {
            const token = window.sessionStorage.getItem('authToken') || window.sessionStorage.getItem('token');
            await POST(`api/exit/clearance/administration/${clearanceData.exitClearanceId}/approve`, { managerSignature: signature }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSnackMessage('Administration clearance approved successfully');
            setSnackColor('success');
            setSnackOpen(true);
            setTimeout(() => { onSuccess(); onClose(); }, 2000);
        } catch (err) {
            setSnackMessage(err.response?.data?.message || 'Approval failed');
            setSnackColor('error');
            setSnackOpen(true);
        } finally {
            setApproveLoading(false);
        }
    };

    const handleNewSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            setSnackMessage('File must be under 2MB');
            setSnackColor('error');
            setSnackOpen(true);
            return;
        }
        const reader = new FileReader();
        reader.onloadend = () => setNewSignature(reader.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveNewSignature = () => setNewSignature(null);

    const handleSignatureConfirm = async () => {
        if (signatureChoice === 'new' && !newSignature) {
            setSnackMessage('Please upload a signature');
            setSnackColor('error');
            setSnackOpen(true);
            return;
        }
        setOpenSignatureDialog(false);
        const sig = signatureChoice === 'existing' ? existingSignature : newSignature;
        await performUpdate(sig, true);
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
            <DialogTitle sx={{ p: 0 }}>
                <MDBox variant="gradient" sx={{ background: properties.colors.bgcolorbluethem }} borderRadius="lg" p={3} textAlign="center">
                    <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>Administration Clearance Form</MDTypography>
                    <MDTypography variant="body2" color="white" opacity={0.8}>Update and approve clearance</MDTypography>
                    <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12, color: 'white' }}><Close /></IconButton>
                </MDBox>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                <MDBox pt={4} pb={3}>
                    <Card>
                        <MDBox pt={4} pb={3} px={3}>
                            {isAlreadyApproved && (
                                <Card sx={{ p: 2, mb: 3, backgroundColor: '#fff3cd', border: '2px solid #ffc107' }}>
                                    <MDBox display="flex" alignItems="center">
                                        <Warning sx={{ color: '#ff9800', mr: 1 }} />
                                        <MDTypography variant="body2" fontWeight="bold">
                                            This clearance was previously approved. Changes require re-approval with signature.
                                        </MDTypography>
                                    </MDBox>
                                </Card>
                            )}

                            <Card
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    backgroundColor: '#fff3cd',
                                    border: '1px solid #ffc107',
                                }}
                            >
                                <MDTypography variant="body2" color="text" textAlign="center" fontWeight="medium">
                                    An employee is required to return the Company&apos;s properties to the respective Department&apos;s before leaving the Company. He/She is instructed to bring the form to the various Departments as stated below to obtain Clearance before exit.
                                </MDTypography>
                            </Card>

                            {/* Employee Info Cards */}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', mb: 2 }}>
                                        <MDBox p={3}>
                                            <MDBox display="flex" alignItems="center" mb={2}>
                                                <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                                                <MDTypography variant="h6" fontWeight="bold" color="info">Personal Information</MDTypography>
                                            </MDBox>
                                            <Grid container spacing={2.5}>
                                                {['employeeName', 'employeeId', 'designation', 'technology'].map((field, i) => (
                                                    <Grid item xs={12} md={3} key={i}>
                                                        <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                                            {field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                        </MDTypography>
                                                        <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                                            {clearanceData?.[field] || 'N/A'}
                                                        </MDTypography>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </MDBox>
                                    </Card>

                                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0', mb: 2 }}>
                                        <MDBox p={3}>
                                            <MDBox display="flex" alignItems="center" mb={2}>
                                                <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                                                <MDTypography variant="h6" fontWeight="bold" color="info">
                                                    Project Details
                                                </MDTypography>
                                            </MDBox>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} md={4}>
                                                    <MDBox>
                                                        <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                                            Project Name
                                                        </MDTypography>
                                                        <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                                            {clearanceData?.projectName || 'N/A'}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <MDBox>
                                                        <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                                            Reporting Manager
                                                        </MDTypography>
                                                        <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                                            {clearanceData?.projectManager || 'N/A'}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <MDBox>
                                                        <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
                                                            Knowledge Transfer
                                                        </MDTypography>
                                                        <MDTypography variant="h6" fontWeight="medium" mt={0.5}>
                                                            {clearanceData?.knowledgeTransfer || 'N/A'}
                                                        </MDTypography>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        </MDBox>
                                    </Card>

                                    <Card sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                                        <MDBox p={3}>
                                            <MDBox display="flex" alignItems="center" mb={2}>
                                                <Box sx={{ width: 4, height: 24, backgroundColor: '#1A73E8', borderRadius: 1, mr: 1.5 }} />
                                                <MDTypography variant="h6" fontWeight="bold" color="info">Exit Information</MDTypography>
                                            </MDBox>
                                            <Grid container spacing={2.5}>
                                                <Grid item xs={12} md={4}>
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>Date of Joining</MDTypography>
                                                    <MDTypography variant="h6" mt={0.5}>
                                                        {clearanceData?.dateOfJoining ? new Date(clearanceData.dateOfJoining).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                                    </MDTypography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>Date of Leaving</MDTypography>
                                                    <MDTypography variant="h6" mt={0.5}>
                                                        {clearanceData?.dateOfLeaving ? new Date(clearanceData.dateOfLeaving).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                                                    </MDTypography>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>Notice Period Served</MDTypography>
                                                    <MDTypography variant="h6" mt={0.5}>{clearanceData?.noticePeriodServed || 'N/A'} days</MDTypography>
                                                </Grid>
                                            </Grid>
                                        </MDBox>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{ p: 2, mb: 2, backgroundColor: '#e7f3ff', border: '1px solid #2196f3' }}>
                                        <MDTypography variant="body2" color="text" fontWeight="medium">
                                            Person in-charge is required to sign-off in the respective sections mentioned below. Advising team and Reporting Manager should sign after rest of the clearances.
                                        </MDTypography>
                                    </Card>
                                </Grid>

                                {/* Administration Clearance Section */}
                                <Grid item xs={12}>
                                    <Card sx={{ p: 2.5, mb: 2, backgroundColor: '#f5f7fa', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                                        <MDTypography variant="h6" fontWeight="bold">Administration Clearance</MDTypography>
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={12} md={3}><Typography variant="body2" fontWeight="bold">Particulars</Typography></Grid>
                                            <Grid item xs={12} md={2}><Typography variant="body2" fontWeight="bold">Status<span style={{ color: '#d32f2f' }}>*</span></Typography></Grid>
                                            <Grid item xs={12} md={3}><Typography variant="body2" fontWeight="bold">Employee Comment</Typography></Grid>
                                            <Grid item xs={12} md={4}><Typography variant="body2" fontWeight="bold">Department Comment<span style={{ color: '#d32f2f' }}>*</span></Typography></Grid>

                                            {/* ID Card */}
                                            <Grid item xs={12} md={3}><Typography variant="body2">Id card</Typography></Grid>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth variant="standard" error={!!validationErrors.idCard}>
                                                    <InputLabel>Select Status</InputLabel>
                                                    <Select value={formData.idCard} onChange={e => handleChange('idCard', e.target.value)} disabled={readOnly}>
                                                        {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                                        <MenuItem value="N/A">N/A</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                                    </Select>
                                                    {validationErrors.idCard && <FormHelperText>Required</FormHelperText>}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth variant="standard" value={clearanceData?.empIdCardComment || ''} disabled
                                                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField fullWidth variant="standard" placeholder="Enter department comment"
                                                    value={formData.departmentIdCardComment} onChange={e => handleChange('departmentIdCardComment', e.target.value)}
                                                    error={validationErrors.departmentIdCardComment}
                                                    helperText={validationErrors.departmentIdCardComment ? 'Department comment is required' : ''} disabled={readOnly}
                                                />

                                            </Grid>

                                            {/* Drawer Keys */}
                                            <Grid item xs={12} md={3}><Typography variant="body2">Drawer keys</Typography></Grid>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth variant="standard" error={!!validationErrors.drawerKeys}>
                                                    <InputLabel>Select Status</InputLabel>
                                                    <Select value={formData.drawerKeys} onChange={e => handleChange('drawerKeys', e.target.value)} disabled={readOnly}>
                                                        {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                                        <MenuItem value="N/A">N/A</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                                    </Select>
                                                    {validationErrors.drawerKeys && <FormHelperText>Required</FormHelperText>}
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth variant="standard" value={clearanceData?.empDrawerKeysComment || ''} disabled
                                                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField fullWidth variant="standard" placeholder="Enter department comment"
                                                    value={formData.departmentDrawerKeysComment} onChange={e => handleChange('departmentDrawerKeysComment', e.target.value)}
                                                    error={validationErrors.departmentDrawerKeysComment}
                                                    helperText={validationErrors.departmentDrawerKeysComment ? 'Department comment is required' : ''} disabled={readOnly} />
                                            </Grid>

                                            {/* Access Card */}
                                            {/* <Grid item xs={12} md={3}><Typography variant="body2">Access cards / Biometric</Typography></Grid>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth variant="standard" error={!!validationErrors.accessCardDeleted}>
                                                    <InputLabel>Select Status</InputLabel>
                                                    <Select value={formData.accessCardDeleted} onChange={e => handleChange('accessCardDeleted', e.target.value)} disabled={readOnly}> */}
                                                        {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                                        {/* <MenuItem value="N/A">N/A</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth variant="standard" value={clearanceData?.empAccessCardComment || ''} disabled
                                                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField fullWidth variant="standard" placeholder="Enter department comment"
                                                    value={formData.departmentAccessCardComment} onChange={e => handleChange('departmentAccessCardComment', e.target.value)}
                                                    error={validationErrors.departmentAccessCardComment}
                                                    helperText={validationErrors.departmentAccessCardComment ? 'Department comment is required' : ''} disabled={readOnly} />
                                            </Grid> */}

                                            {/* Company Mobile */}
                                            {/* <Grid item xs={12} md={3}><Typography variant="body2">Company mobile phone</Typography></Grid>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth variant="standard" error={!!validationErrors.companyMobile}>
                                                    <InputLabel>Select Status</InputLabel>
                                                    <Select value={formData.companyMobile} onChange={e => handleChange('companyMobile', e.target.value)} disabled={readOnly}> */}
                                                        {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                                        {/* <MenuItem value="N/A">N/A</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth variant="standard" value={clearanceData?.empCompanyMobileComment || ''} disabled
                                                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField fullWidth variant="standard" placeholder="Enter department comment"
                                                    value={formData.departmentCompanyMobileComment} onChange={e => handleChange('departmentCompanyMobileComment', e.target.value)}
                                                    error={validationErrors.departmentCompanyMobileComment}
                                                    helperText={validationErrors.departmentCompanyMobileComment ? 'Department comment is required' : ''} disabled={readOnly} />
                                            </Grid> */}

                                            {/* Stationery */}
                                            <Grid item xs={12} md={3}><Typography variant="body2">Stationery</Typography></Grid>
                                            <Grid item xs={12} md={2}>
                                                <FormControl fullWidth variant="standard" error={!!validationErrors.stationery}>
                                                    <InputLabel>Select Status</InputLabel>
                                                    <Select value={formData.stationery} onChange={e => handleChange('stationery', e.target.value)} disabled={readOnly}>
                                                        {/* <MenuItem value="" disabled>Select</MenuItem> */}
                                                        <MenuItem value="N/A">N/A</MenuItem>
                                                        <MenuItem value="Pending">Pending</MenuItem>
                                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                                <TextField fullWidth variant="standard" value={clearanceData?.empStationeryComment || ''} disabled
                                                    sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0,0,0,0.6)', backgroundColor: '#f5f5f5' } }} />
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <TextField fullWidth variant="standard" placeholder="Enter department comment"
                                                    value={formData.departmentStationeryComment} onChange={e => handleChange('departmentStationeryComment', e.target.value)}
                                                    error={validationErrors.departmentStationeryComment}
                                                    helperText={validationErrors.departmentStationeryComment ? 'Department comment is required' : ''} disabled={readOnly} />
                                            </Grid>

                                            {/* Additional Items */}
                                            <Grid item xs={12}>
                                                <AdditionalItemsSection
                                                    items={formData.additionalItems}
                                                    handleAdditionalItemChange={handleAdditionalChange}
                                                    handleRemoveAdditionalItem={handleRemoveAdditional}
                                                    handleAddAdditionalItem={handleAddAdditional}
                                                    validationErrors={validationErrors}
                                                    disabled={readOnly}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Card>
                                </Grid>

                                {/* Approved Badge */}
                                {existingSignature && (
                                    <Grid item xs={12}>
                                        <Card sx={{ p: 3, mb: 3, backgroundColor: '#e8f5e8', border: '2px solid #4caf50', borderRadius: 2 }}>
                                            <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                                <MDBox display="flex" alignItems="center">
                                                    <CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32', mr: 2 }} />
                                                    <MDBox>
                                                        <MDTypography variant="h6" fontWeight="bold" color="success.dark">Clearance Approved</MDTypography>
                                                        <MDTypography variant="body2">This Administration clearance has been approved.</MDTypography>
                                                    </MDBox>
                                                </MDBox>
                                                <MDBox sx={{ p: 2, bgcolor: 'white', borderRadius: 2, border: '1px solid #a5d6a7' }}>
                                                    <img
                                                        src={existingSignature.startsWith('data:') ? existingSignature : `data:image/png;base64,${existingSignature}`}
                                                        alt="Signature" style={{ maxHeight: '80px', maxWidth: '250px', objectFit: 'contain' }}
                                                    />
                                                </MDBox>
                                            </MDBox>
                                        </Card>
                                    </Grid>
                                )}

                                {/* Action Buttons */}
                                {!readOnly && (
                                <Grid item xs={12}>
                                    <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                                        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={updateLoading || approveLoading}>Cancel</MDButton>
                                        <MDButton variant="gradient" color="info" type="submit" onClick={(e) => handleSubmit(e, false)}
                                            disabled={updateLoading || approveLoading || readOnly} sx={{ background: properties.colors.bgcolorbluethem, ml: 2 }}>
                                            {updateLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} /> Updating...</> : 'Update Clearance Form'}
                                        </MDButton>
                                        <MDButton variant="gradient" color="success" onClick={(e) => handleSubmit(e, true)}
                                            disabled={updateLoading || approveLoading || readOnly} sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', ml: 2 }}>
                                            {approveLoading ? <><CircularProgress size={20} sx={{ mr: 1 }} /> Approving...</> : 'Update & Approve'}
                                        </MDButton>
                                    </DialogActions>
                                </Grid>
                                )}
                            </Grid>
                        </MDBox>
                    </Card>
                </MDBox>
            </DialogContent>

            {/* Simple Signature Dialog (First Approval) */}
            <Dialog open={openSimpleSignatureDialog} onClose={() => setOpenSimpleSignatureDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>Approve Administration Clearance</DialogTitle>
                <DialogContent dividers>
                    <Box textAlign="center" py={4}>
                        {newSignature ? (
                            <Box>
                                <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                                <img src={newSignature} alt="Signature" style={{ maxHeight: 120, border: '2px solid #4caf50', borderRadius: 4 }} />
                                <IconButton onClick={handleRemoveNewSignature} color="error"><Delete /></IconButton>
                            </Box>
                        ) : (
                            <>
                                <CloudUpload sx={{ fontSize: 64, color: '#1976d2', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>Upload Your Signature</Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>Required to approve</Typography>
                                <Button variant="contained" component="label" sx={{ color: '#fff' }} startIcon={<CloudUpload />}>
                                    Upload Signature
                                    <input type="file" hidden accept="image/*" onChange={handleNewSignatureUpload} />
                                </Button>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenSimpleSignatureDialog(false)}>Cancel</Button>
                    <Button variant="contained" color="success" disabled={!newSignature} sx={{
                        backgroundColor: "#1976d2",
                        color: "#fff",
                        '&:hover': {
                            backgroundColor: "#115293",
                        }
                    }} onClick={() => { setOpenSimpleSignatureDialog(false); performUpdate(newSignature, true); }}>
                        Approve Clearance
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Full Re-approval Dialog (Same as IT Form) */}
            <Dialog open={openSignatureDialog} onClose={() => setOpenSignatureDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        py: 3,
                        position: 'relative',
                    }}
                >
                    <MDBox>
                        <MDBox display="flex" alignItems="center" mb={1}>
                            <Warning sx={{ fontSize: 32, mr: 1.5 }} />
                            <MDTypography variant="h4" fontWeight="bold" color="white">
                                Re-approval Required
                            </MDTypography>
                        </MDBox>
                        <MDTypography variant="body2" color="white" sx={{ opacity: 0.95, ml: 5.5 }}>
                            Changes detected - signature verification needed
                        </MDTypography>
                    </MDBox>
                    <IconButton
                        onClick={handleSignatureDialogCancel}
                        size="small"
                        sx={{
                            position: 'absolute',
                            right: 16,
                            top: 16,
                            color: 'white',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,0.3)',
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 4, backgroundColor: '#f8f9fa' }}>
                    {/* Alert Message */}
                    <Alert
                        severity="warning"
                        icon={<Warning />}
                        sx={{
                            mb: 4,
                            borderRadius: 2,
                            border: '1px solid #ffc107',
                            '& .MuiAlert-icon': {
                                fontSize: 28,
                            }
                        }}
                    >
                        <Typography variant="body1" fontWeight="medium" gutterBottom>
                            This clearance was previously approved
                        </Typography>
                        <Typography variant="body2">
                            Since you&apos;ve made changes to the clearance data, you need to re-approve it with a valid signature to maintain audit compliance.
                        </Typography>
                    </Alert>

                    {/* Conditional Rendering based on Employee ID match */}
                    {existingSignature && existingEmpId === empId ? (
                        // Case 1: Same employee - Show choice between existing and new signature
                        <>
                            {/* Show existing signature */}
                            <Card
                                sx={{
                                    mb: 3,
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                }}
                            >
                                <MDBox display="flex" alignItems="center" mb={2}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 24,
                                            backgroundColor: '#667eea',
                                            borderRadius: 1,
                                            mr: 1.5
                                        }}
                                    />
                                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                                        Your Previous Signature
                                    </Typography>
                                </MDBox>

                                <Box
                                    sx={{
                                        display: 'inline-block',
                                        p: 2.5,
                                        border: '2px solid #e0e0e0',
                                        borderRadius: 2,
                                        backgroundColor: '#fafafa',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    <img
                                        src={existingSignature.startsWith('data:')
                                            ? existingSignature
                                            : `data:image/png;base64,${existingSignature}`
                                        }
                                        alt="Existing Signature"
                                        style={{
                                            maxWidth: '350px',
                                            maxHeight: '150px',
                                            display: 'block',
                                        }}
                                    />
                                </Box>
                            </Card>

                            {/* Signature choice */}
                            <Card
                                sx={{
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                }}
                            >
                                <MDBox display="flex" alignItems="center" mb={2}>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 24,
                                            backgroundColor: '#667eea',
                                            borderRadius: 1,
                                            mr: 1.5
                                        }}
                                    />
                                    <Typography variant="h6" fontWeight="bold" color="text.primary">
                                        Choose Signature Option
                                    </Typography>
                                </MDBox>

                                <RadioGroup
                                    value={signatureChoice}
                                    onChange={(e) => setSignatureChoice(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="existing"
                                        control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                                        label={
                                            <Box sx={{ ml: 1 }}>
                                                <Typography variant="body1" fontWeight="medium">
                                                    Use existing signature
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Reuse the signature shown above
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            border: signatureChoice === 'existing' ? '2px solid #667eea' : '2px solid #e0e0e0',
                                            borderRadius: 2,
                                            backgroundColor: signatureChoice === 'existing' ? '#f3f4ff' : 'transparent',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: '#667eea',
                                                backgroundColor: '#f3f4ff',
                                            }
                                        }}
                                    />
                                    <FormControlLabel
                                        value="new"
                                        control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                                        label={
                                            <Box sx={{ ml: 1 }}>
                                                <Typography variant="body1" fontWeight="medium">
                                                    Upload new signature
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Provide a fresh signature image
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{
                                            p: 2,
                                            border: signatureChoice === 'new' ? '2px solid #667eea' : '2px solid #e0e0e0',
                                            borderRadius: 2,
                                            backgroundColor: signatureChoice === 'new' ? '#f3f4ff' : 'transparent',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                borderColor: '#667eea',
                                                backgroundColor: '#f3f4ff',
                                            }
                                        }}
                                    />
                                </RadioGroup>

                                {/* New signature upload section */}
                                {signatureChoice === 'new' && (
                                    <Box sx={{ mt: 3, p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px dashed #667eea' }}>
                                        {newSignature ? (
                                            <Box>
                                                <MDBox display="flex" alignItems="center" mb={2}>
                                                    <CheckCircleIcon sx={{ color: '#4caf50', mr: 1 }} />
                                                    <Typography variant="body1" color="success.main" fontWeight="bold">
                                                        New signature uploaded successfully!
                                                    </Typography>
                                                </MDBox>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        p: 2.5,
                                                        border: '3px solid #4caf50',
                                                        borderRadius: 2,
                                                        backgroundColor: '#f1f8f4',
                                                        position: 'relative',
                                                        boxShadow: '0 4px 12px rgba(76,175,80,0.2)',
                                                    }}
                                                >
                                                    <img
                                                        src={newSignature}
                                                        alt="New Signature"
                                                        style={{
                                                            maxWidth: '350px',
                                                            maxHeight: '150px',
                                                            display: 'block',
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleRemoveNewSignature}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: -12,
                                                            right: -12,
                                                            bgcolor: 'white',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            '&:hover': {
                                                                bgcolor: '#ffebee',
                                                            }
                                                        }}
                                                    >
                                                        <Delete fontSize="small" color="error" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                                <CloudUpload sx={{ fontSize: 56, color: '#667eea', mb: 2 }} />
                                                <Typography variant="h6" color="text.primary" mb={1} fontWeight="medium">
                                                    Upload Your Signature
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" mb={3}>
                                                    Accepted formats: JPG, PNG, GIF (Max 2MB)
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<CloudUpload />}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: '#fff',
                                                        py: 1.5,
                                                        px: 4,
                                                        borderRadius: 2,
                                                        textTransform: 'none',
                                                        fontSize: '1rem',
                                                        fontWeight: 600,
                                                        boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                                            boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
                                                        },
                                                    }}
                                                >
                                                    Choose File
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={handleNewSignatureUpload}
                                                    />
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Card>
                        </>
                    ) : (
                        // Case 2: Different employee - Only show new signature upload
                        <>
                            <Alert
                                severity="info"
                                sx={{
                                    mb: 3,
                                    borderRadius: 2,
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #2196f3',
                                }}
                            >
                                <Typography variant="body2" fontWeight="medium">
                                    Previous signature cannot be reused as it belongs to a different user. Please upload your signature to proceed.
                                </Typography>
                            </Alert>

                            <Card
                                sx={{
                                    p: 3,
                                    backgroundColor: 'white',
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '2px dashed #667eea',
                                }}
                            >
                                {newSignature ? (
                                    <Box>
                                        <MDBox display="flex" alignItems="center" mb={2}>
                                            <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, mr: 1.5 }} />
                                            <Typography variant="h6" color="success.main" fontWeight="bold">
                                                Signature uploaded successfully!
                                            </Typography>
                                        </MDBox>
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                p: 2.5,
                                                border: '3px solid #4caf50',
                                                borderRadius: 2,
                                                backgroundColor: '#f1f8f4',
                                                position: 'relative',
                                                boxShadow: '0 4px 12px rgba(76,175,80,0.2)',
                                            }}
                                        >
                                            <img
                                                src={newSignature}
                                                alt="New Signature"
                                                style={{
                                                    maxWidth: '350px',
                                                    maxHeight: '150px',
                                                    display: 'block',
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveNewSignature}
                                                sx={{
                                                    position: 'absolute',
                                                    top: -12,
                                                    right: -12,
                                                    bgcolor: 'white',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                    '&:hover': {
                                                        bgcolor: '#ffebee',
                                                    }
                                                }}
                                            >
                                                <Delete fontSize="small" color="error" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <CloudUpload sx={{ fontSize: 64, color: '#667eea', mb: 2 }} />
                                        <Typography variant="h5" color="text.primary" mb={1} fontWeight="bold">
                                            Upload Your Signature
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" mb={3}>
                                            Please provide your signature to re-approve this clearance
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={3}>
                                            Accepted formats: JPG, PNG, GIF • Maximum size: 2MB
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            component="label"
                                            startIcon={<CloudUpload />}
                                            sx={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                py: 1.5,
                                                px: 5,
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                fontSize: '1.1rem',
                                                fontWeight: 600,
                                                boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                                    boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
                                                    transform: 'translateY(-2px)',
                                                },
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            Choose Signature File
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={handleNewSignatureUpload}
                                            />
                                        </Button>
                                    </Box>
                                )}
                            </Card>
                        </>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 3,
                        backgroundColor: 'white',
                        borderTop: '1px solid #e0e0e0',
                        gap: 2,
                    }}
                >
                    <MDButton
                        variant="outlined"
                        color="secondary"
                        onClick={handleSignatureDialogCancel}
                        sx={{
                            borderWidth: 2,
                            borderColor: '#9e9e9e',
                            color: '#616161',
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            '&:hover': {
                                borderWidth: 2,
                                borderColor: '#757575',
                                backgroundColor: '#f5f5f5',
                            }
                        }}
                    >
                        Cancel
                    </MDButton>
                    <MDButton
                        variant="gradient"
                        color="info"
                        onClick={handleSignatureDialogConfirm}
                        disabled={
                            loading ||
                            (existingEmpId === empId && signatureChoice === 'new' && !newSignature) ||
                            (existingEmpId !== empId && !newSignature)
                        }
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            px: 4,
                            py: 1,
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 600,
                            boxShadow: '0 4px 12px rgba(102,126,234,0.4)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                                boxShadow: '0 6px 16px rgba(102,126,234,0.5)',
                            },
                            '&:disabled': {
                                background: '#e0e0e0',
                                color: '#9e9e9e',
                            }
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                Processing...
                            </>
                        ) : (
                            <>
                                <CheckCircleIcon sx={{ mr: 1, fontSize: 20 }} />
                                Confirm & Re-approve
                            </>
                        )}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackOpen}
                autoHideDuration={6000}
                onClose={() => setSnackOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackOpen(false)}
                    severity={snackColor === 'success' ? 'success' : 'error'}
                    variant="filled"
                    sx={{
                        whiteSpace: 'pre-line',
                        maxWidth: '500px',
                        color: '#fff'
                    }}
                >
                    {snackMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default AdministrationClearanceUpdateForm;